import { useEffect, useRef } from 'react';

import { useAppDispatch } from 'hooks/redux';
import * as AuthService from 'modules/auth/services/AuthService';
import { authenticateFailed } from 'modules/auth/slices/authSlice';
import { isInIframe } from 'utils/httpUtil';

/**
 * UAT-3254 — a microsite is fetched once, on mount (`PrivateRoute`'s own
 * effect), and never again: if a broker changes the URL slug while a visitor
 * already has the old link open, nothing tells that tab. The visitor keeps
 * reading until they happen to refresh, at which point the SAME fetch that
 * loaded the guide now 404s (or returns whichever `benefit guide invalidated`
 * / `archived-deleted` code applies) and `PrivateRoute` already renders the
 * right "no longer available" screen for it — see `authenticateFailed`. This
 * hook is that fetch, run again on an interval, so the existing screen shows
 * up in seconds instead of waiting for an accidental reload.
 *
 * UAT-3260 — symmetric with the above: a guide moved OUT of "published"
 * (draft/in-progress/archived) can just as easily move back IN while the
 * same reader still has the error screen open (an admin published it a
 * moment after un-publishing, or undid an archive). `onRecovered` is how
 * the caller finds out a poll succeeded — see `PrivateRoute`, which only
 * acts on it while currently showing the "not published" screen, so an
 * ordinary already-healthy poll stays a no-op.
 */
export const LIVENESS_POLL_INTERVAL_MS = 5_000;

/**
 * Only a definitive "the server said no" (a 4xx response) is worth acting
 * on. A network blip or a 5xx has no response body to classify, and
 * `authenticateFailed` would otherwise read that shape as `notFound` (see
 * its default branch) — which would boot a reader with a bad wifi connection
 * to an "unavailable" screen for a guide that is perfectly fine. Same
 * "fails open" discipline as the Flipbook viewer's `useAccessWatch`.
 *
 * @param {unknown} error the caught rejection from the liveness fetch
 * @return {boolean} whether the server gave a definitive 4xx answer
 */
const isDefinitiveClientError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
};

/**
 * @param {string} hash the microsite's url-hash, exactly as already used to
 *   load it
 * @param {boolean} enabled only poll while there is something worth
 *   confirming: the guide is actually loaded, and nothing already showing
 *   is a TERMINAL state (`notFound`/`invalidated`/`authFailed` — see
 *   `PrivateRoute`, which deliberately keeps polling through a transient
 *   "not published" screen so it can notice a recovery, UAT-3260).
 * @param {Function} [onRecovered] called with the fresh response body
 *   whenever a check succeeds. Fires on every successful poll, not only
 *   after an error — the caller decides whether there is anything to do
 *   with it (see `PrivateRoute`'s own guard).
 */
export const useMicrositeLivenessWatch = (
  hash: string,
  enabled: boolean,
  onRecovered?: (data: unknown) => void
): void => {
  const dispatch = useAppDispatch();

  // Ref'd so a fresh `onRecovered` identity on every render (PrivateRoute
  // passes an inline closure) cannot restart the interval — same reason
  // the Flipbook viewer's useAccessWatch refs its callbacks instead of
  // listing them as effect deps.
  const onRecoveredRef = useRef(onRecovered);
  useEffect(() => {
    onRecoveredRef.current = onRecovered;
  }, [onRecovered]);

  useEffect(() => {
    if (!enabled || !hash) return undefined;

    let cancelled = false;

    const check = async () => {
      // Nothing to learn from a hidden tab; the visibility listener below
      // re-checks the moment it stops being hidden.
      if (cancelled || document.hidden) return;

      try {
        // The exact same call `initBenguideDataByHash` makes on first load —
        // `isInIframe()` and all (UAT-3259 follow-up: this used to hardcode
        // `false`, which silently downgraded every poll from an
        // authenticated in-iframe edit session to the public, non-edit
        // endpoint. `getBenguideByHash` branches on that flag to hit
        // `/edit-mode` instead of the public hash route — dropping it made
        // the poll see an in-progress guide as "not published" every 5s and
        // boot the editor to the same screen a real reader gets, mid-edit).
        // Including its one-retry-after-401/403 guest-token dance via
        // `attemptPublicAuthentication`, so a poll never misclassifies a
        // routine token refresh as an invalidation.
        const response = await AuthService.attemptPublicAuthentication(
          hash,
          AuthService.getBenguideByHash,
          hash,
          isInIframe()
        );
        if (!cancelled) onRecoveredRef.current?.(response.data);
      } catch (error) {
        if (!cancelled && isDefinitiveClientError(error)) {
          // Reuses the exact classification `authenticateFailed` already
          // applies on a manual refresh — this poll does not invent a new
          // error taxonomy, it just asks the same question sooner.
          dispatch(authenticateFailed(error));
        }
      }
    };

    const interval = window.setInterval(check, LIVENESS_POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (!document.hidden) check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [hash, enabled, dispatch]);
};
